import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { ToggleSwitch } from '../../../../../common/components/ToggleSwitch/ToggleSwitch';
import BasicModalTemplate from '../../../../../common/modals/BasicModalTemplate';
import { ModalInstanceProps } from '../../../../../common/modals/interfaces';
import { ModalProcessingBlinder } from '../../../../../common/modals/modal-helpers';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import {
  PlanSubtypes,
  PlanTypeBE,
  SubscriptionResponse,
} from '@mm/state/interfaces/subscription';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import modalActions, {
  OpenBillingModalParams,
} from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPagePlanInfoSelector,
  selectPlansMapping,
} from '@mm/state/selectors/facebookPageSelector';
import {
  selectAccountDetails,
  enhancedPlansSelector,
} from '@mm/state/selectors/subscriptionSelectors';
import {
  activeCompanySelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import './BillingModal.pcss';
import { BillingModalHeader } from './BillingModalHeader/BillingModalHeader';
import CheckoutForm from './CheckoutForm/CheckoutForm';

type OuterProps = OpenBillingModalParams &
  ModalInstanceProps & {
    updateAllotment: (
      selectedLevel: number,
      subscriptionId: number,
      companyId: number,
    ) => void;
    level: {
      id: number;
      name: string;
      level: number;
    };
  };

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const useFetchAccountDetails = (
  props: Props & { accountDetails?: SubscriptionResponse },
) => {
  const { accountDetails, company, getAccountData, paywall } = props;
  const [fetchingAccountDetails, setFetchingAccountDetails] = useState(true);

  useEffect(() => {
    const fetchAccountDetails = async () => {
      if (company) {
        await getAccountData(company.id);
      }
      setFetchingAccountDetails(false);
    };

    if (accountDetails) {
      setFetchingAccountDetails(false);
    } else {
      fetchAccountDetails();
    }
  }, [accountDetails, company, getAccountData]);

  return fetchingAccountDetails;
};

const BillingModal = (props: Props) => {
  const {
    closeModal,
    plan,
    facebookPage,
    openSelectTeamPagesModal,
    company,
    upgradeFromSubscriptionId,
    downgradeFromSubscriptionId,
    attachPagesTeam,
    landmineOpenId,
    updateAllotment,
    level,
    paywall,
    seatsObject,
    phoneNumber,
    subscription,
    stayOnPage,
    upgradetoTeam,
    navigatedFrom,
    smsActivation,
    onSuccessBilling,
    plansMapping,
    onPlanCreatedFailed,
    isMonthly = false,
    hideAnnualToggle,
    skipFetchActivePage,
    extraSendsOnly,
    alert,
    manualRedirect,
  } = props;

  const accountDetails = useSelector(selectAccountDetails);
  const fetchingAccountDetails = useFetchAccountDetails({
    ...props,
    accountDetails,
  });
  const activeFacebookPagePlanInfo = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).info;
  const [isFetching, setIsFetching] = useState(false);
  const {
    match: {
      params: { chatbotId },
    },
  } = useRouter<{ chatbotId: string }>();
  const dispath = useDispatch();
  const editable = company && company.editable;
  const companyId = useSelector(selectActiveCompanyId);

  const isBilling2023PlanSubtype = plan.planSubtypes?.find(
    subtype => subtype === PlanSubtypes.billing2023,
  );
  const mappingKeyToUse =
    plan.planType === 'pro' ? plan.planSubtypes?.[0] : plan.planType;

  const planCodesOfSameTypeAsArrayOrString = plansMapping?.[mappingKeyToUse];
  const planCodesOfSameType = Array.isArray(planCodesOfSameTypeAsArrayOrString)
    ? planCodesOfSameTypeAsArrayOrString
    : [planCodesOfSameTypeAsArrayOrString];

  const enhancedPlans = useSelector(enhancedPlansSelector);

  const getEnhancedPlan = billingPeriod =>
    !isBilling2023PlanSubtype
      ? enhancedPlans
          .filter(p => planCodesOfSameType.includes(p.planCode))
          .find(p =>
            billingPeriod === 'month' && p.planType === 'team'
              ? p.planCode === plansMapping?.actualTeam
              : p.billingPeriod === billingPeriod,
          )
      : enhancedPlans.find(
          p =>
            p.planCode.includes(plan.planCode.replace('_annual', '')) &&
            p.billingPeriod === billingPeriod,
        );

  const hasAnnualPlan = !!getEnhancedPlan('year');

  const isCurrentPlanAnnual = plan.billingPeriod === 'year';
  const isInitialPlanAnnual =
    hasAnnualPlan && !isMonthly && isCurrentPlanAnnual;

  const [isAnnual, setIsAnnual] = useState(isInitialPlanAnnual);

  const getPlanToUse = () => {
    if (!isCurrentPlanAnnual && isAnnual) {
      return getEnhancedPlan('year');
    }
    if (isCurrentPlanAnnual && !isAnnual) {
      return getEnhancedPlan('month');
    }
    return plan;
  };

  const planToUse = getPlanToUse() || plan;

  useEffect(() => {
    if (
      (facebookPage &&
        (upgradeFromSubscriptionId || downgradeFromSubscriptionId) &&
        companyId) ||
      upgradetoTeam
    ) {
      const id = upgradeFromSubscriptionId
        ? upgradeFromSubscriptionId
        : downgradeFromSubscriptionId
        ? downgradeFromSubscriptionId
        : 0;
      const isUpgrade =
        planToUse.planType === PlanTypeBE.team
          ? true
          : !!upgradeFromSubscriptionId;

      dispath(
        subscriptionCRUDActions.getCreditsForFuturePayments(
          companyId,
          planToUse.planCode,
          id,
          isUpgrade,
        ),
      );
    }
    if (isEmpty(activeFacebookPagePlanInfo)) {
      dispath(
        connectedPageCRUDActions.retrieveAll({
          botId: facebookPage?.activeBotId || Number(chatbotId),
        }),
      );
    }
  }, []);

  const onPlanCreatedCallback = (
    subscription?: SubscriptionModel,
    closeModalCalled?: boolean,
  ) => {
    if (onSuccessBilling) {
      onSuccessBilling();
    }
    if (closeModalCalled) {
      closeModal();
      return;
    }
    if (
      planToUse.planType === PlanTypeBE.team &&
      subscription &&
      planToUse.planSubtypes.some(subType => subType === PlanSubtypes.team2019)
    ) {
      openSelectTeamPagesModal(subscription);
    } else {
      if (
        company &&
        subscription &&
        subscription.planType === PlanTypeBE.team
      ) {
        attachPagesTeam(company.id, subscription?.id, [0]);
      }
      closeModal();
    }
  };

  return (
    <BasicModalTemplate
      className="billing-modal"
      withMonkeyBackground={false}
      hideCloseButton={paywall}
    >
      {!smsActivation && (
        <BillingModalHeader
          facebookPage={facebookPage}
          plan={planToUse}
          company={company}
        />
      )}

      {/* Note: First impolementation is for new subscriptions only*/}
      {!hideAnnualToggle && !subscription && hasAnnualPlan && !smsActivation && (
        <div className="billing-modal__toggle-section-constainer">
          <div className="billing-modal__toggle-row">
            <ToggleSwitch
              isOn={isAnnual}
              handleToggle={() => setIsAnnual(!isAnnual)}
              leftSwitchText="Monthly"
              rightSwitchtext="Annual"
              classes={{
                container: 'billing-modal__toggle-container',
                trigger: 'billing-modal__toggle-trigger',
                activeTrigger: 'billing-modal__toggle-active-trigger',
              }}
            />
          </div>
        </div>
      )}

      {!fetchingAccountDetails && company && editable && (
        <CheckoutForm
          seatsObject={seatsObject}
          setIsFetching={setIsFetching}
          upgradeFromSubscriptionId={upgradeFromSubscriptionId}
          downgradeFromSubscriptionId={downgradeFromSubscriptionId}
          facebookPage={facebookPage}
          onPlanCreatedCallback={onPlanCreatedCallback}
          plan={planToUse}
          paywall={paywall}
          company={company}
          landmineOpenId={landmineOpenId}
          updateAllotment={updateAllotment}
          level={level}
          phoneNumber={phoneNumber}
          subscription={subscription}
          activeFacebookPagePlanInfo={activeFacebookPagePlanInfo}
          stayOnPage={stayOnPage}
          navigatedFrom={navigatedFrom}
          smsActivation={smsActivation}
          isAnnual={isAnnual}
          onPlanCreatedFailed={onPlanCreatedFailed}
          skipFetchActivePage={skipFetchActivePage}
          extraSendsOnly={extraSendsOnly}
          alert={alert}
          manualRedirect={manualRedirect}
        />
      )}
      {!editable && (
        <div className="billing-modal__only-owner">
          Only the account Owner or Admin can Upgrade Pages
        </div>
      )}

      <ModalProcessingBlinder
        show={fetchingAccountDetails || isFetching}
        className="billing-modal__blinder"
      />
    </BasicModalTemplate>
  );
};

const mapStateToProps = (state: StoreState) => ({
  company: activeCompanySelector(state),
  plansMapping: selectPlansMapping(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openSelectTeamPagesModal: (subscription: SubscriptionModel) =>
    dispatch(modalActions.openSelectTeamPagesModal({ subscription })),
  getAccountData: (companyId: number) =>
    dispatch(subscriptionCRUDActions.getAccountData(companyId)),
  attachPagesTeam: (companyId: number, subscriptionId: number, ids: number[]) =>
    dispatch(
      connectedPageCRUDActions.attachPagesTeam(companyId, subscriptionId, ids),
    ),
});

const enhance = compose<OuterProps, InnerProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(BillingModal);
